export interface Mailbox {
  name?: string;
  address: string;
}

const EMAIL_RE = /^[^\s@<>(),;:"]+@[^\s@<>(),;:"]+\.[^\s@<>(),;:"]{2,}$/;

export function isValidEmail(value: string): boolean {
  return EMAIL_RE.test(value.trim());
}

/** Splits an RFC 5322-ish address list, honouring quotes and angle brackets: `"Doe, Jane" <j@x.com>, b@y.com`. */
export function parseAddressList(input: string | undefined | null): Mailbox[] {
  if (!input) return [];
  const parts: string[] = [];
  let cur = '';
  let quoted = false;
  let angle = 0;
  for (const ch of input) {
    if (ch === '"') quoted = !quoted;
    else if (!quoted && ch === '<') angle++;
    else if (!quoted && ch === '>') angle = Math.max(0, angle - 1);
    if ((ch === ',' || ch === ';') && !quoted && angle === 0) {
      parts.push(cur);
      cur = '';
    } else cur += ch;
  }
  parts.push(cur);
  return parts.map(parseMailbox).filter((m): m is Mailbox => m !== null);
}

export function parseMailbox(raw: string): Mailbox | null {
  const s = raw.trim();
  if (!s) return null;
  const m = s.match(/^(.*?)<\s*([^<>\s]+@[^<>\s]+)\s*>\s*$/);
  if (m) {
    const name = m[1].trim().replace(/^"(.*)"$/, '$1').replace(/\\"/g, '"').trim();
    return { name: name || undefined, address: m[2].toLowerCase() };
  }
  const bare = s.replace(/^mailto:/i, '');
  if (bare.includes('@')) return { address: bare.toLowerCase() };
  return null;
}

export function formatMailbox(m: Mailbox): string {
  if (!m.name || m.name.toLowerCase() === m.address.toLowerCase()) return m.address;
  const needsQuotes = /[",;<>@()]/.test(m.name);
  return `${needsQuotes ? `"${m.name.replace(/"/g, '\\"')}"` : m.name} <${m.address}>`;
}

export function domainOf(address: string | undefined | null): string {
  if (!address) return '';
  const at = address.lastIndexOf('@');
  return at >= 0 ? address.slice(at + 1).toLowerCase().replace(/[>\s]/g, '') : '';
}

/** Friendly name: the display name, else a prettified local part ("jane.doe" -> "Jane Doe"). */
export function displayName(m: Mailbox | undefined | null): string {
  if (!m) return '';
  if (m.name) return m.name;
  const local = m.address.split('@')[0] ?? m.address;
  if (/^(no-?reply|notifications?|info|support|hello|team|mailer-daemon)$/i.test(local)) {
    const d = domainOf(m.address).split('.');
    const brand = d.length > 1 ? d[d.length - 2] : d[0];
    return brand ? brand.charAt(0).toUpperCase() + brand.slice(1) : m.address;
  }
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function firstName(nameOrAddress: string): string {
  const m = parseMailbox(nameOrAddress) ?? { address: nameOrAddress };
  return displayName(m).split(/\s+/)[0] ?? '';
}

export function initials(name: string): string {
  const words = name.replace(/[^\p{L}\p{N}\s]/gu, ' ').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

/** Consumer mailbox providers: their favicon says nothing about the sender, so show initials instead. */
export const FREEMAIL_DOMAINS = new Set([
  'gmail.com', 'googlemail.com', 'outlook.com', 'hotmail.com', 'live.com', 'msn.com', 'yahoo.com', 'ymail.com',
  'icloud.com', 'me.com', 'mac.com', 'aol.com', 'proton.me', 'protonmail.com', 'gmx.com', 'gmx.net', 'mail.com',
  'zoho.com', 'yandex.com', 'yandex.ru', 'fastmail.com', 'hey.com', 'comcast.net', 'att.net', 'verizon.net',
  'sbcglobal.net', 'bellsouth.net', 'cox.net', 'charter.net', 'qq.com', '163.com', 'web.de', 'orange.fr',
]);

export function isFreemail(domain: string): boolean {
  return FREEMAIL_DOMAINS.has(domain.toLowerCase());
}

/** Stable hue from a string, for avatar gradients. */
export function hueOf(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return h % 360;
}

export function uniqueByAddress(list: Mailbox[]): Mailbox[] {
  const seen = new Set<string>();
  return list.filter((m) => {
    const k = m.address.toLowerCase();
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

/** Trusted-app link subject: "<siteDomain>|<email>", both trimmed and lower-cased (matches the server and RealEstate API). */
export function appLinkSubject(siteDomain: string, email: string): string {
  return `${siteDomain.trim().toLowerCase()}|${email.trim().toLowerCase()}`;
}
